import React, { useEffect, useState, useCallback } from 'react';
import apiService from '../apiService';

/**
 * « À l'affiche » de la Billetterie (2026-11-04) : par ville, jusqu'à 5 événements à venir, ordonnés, date de fin
 * facultative. L'app client les montre en carrousel en tête de la Billetterie, complétés par les prochains
 * événements de la ville. Un événement passé, annulé ou complet disparaît tout seul de l'app.
 */
const MAX = 5;
const fmt = (d) => { try { return new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return '—'; } };
const toLocalInput = (d) => { if (!d) return ''; const x = new Date(d); const z = (n) => String(n).padStart(2, '0'); return `${x.getFullYear()}-${z(x.getMonth() + 1)}-${z(x.getDate())}T${z(x.getHours())}:${z(x.getMinutes())}`; };

export default function EventsFeaturedPanel() {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState('');
  const [picks, setPicks] = useState([]);           // [{ event_id, ends_at, event, live }]
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    apiService.get('/cities', { userType: 'admin' }).then((r) => {
      const list = r?.cities || [];
      setCities(list);
      if (list.length && !city) setCity((list.find((c) => c.is_main_city) || list[0]).slug);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    if (!city) return;
    setLoading(true); setMsg(null);
    try {
      const r = await apiService.get(`/admin/events/featured?city=${encodeURIComponent(city)}`, { userType: 'admin' });
      setPicks(r?.success ? r.picks : []); setCandidates(r?.success ? r.candidates : []); setDirty(false);
    } catch (e) { setMsg({ err: true, text: e?.error || 'Chargement impossible' }); }
    finally { setLoading(false); }
  }, [city]);
  useEffect(() => { load(); }, [load]);

  const pickedIds = new Set(picks.map((p) => p.event_id));
  const add = (ev) => { if (picks.length >= MAX) return; setPicks((l) => [...l, { event_id: ev.event_id, ends_at: null, event: ev, live: ev.status === 'published' }]); setDirty(true); };
  const remove = (id) => { setPicks((l) => l.filter((p) => p.event_id !== id)); setDirty(true); };
  const move = (i, d) => { setPicks((l) => { const c = [...l]; const j = i + d; if (j < 0 || j >= c.length) return l; [c[i], c[j]] = [c[j], c[i]]; return c; }); setDirty(true); };
  const setEnd = (id, v) => { setPicks((l) => l.map((p) => (p.event_id === id ? { ...p, ends_at: v ? new Date(v).toISOString() : null } : p))); setDirty(true); };

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const r = await apiService.put('/admin/events/featured', { city_slug: city, items: picks.map((p) => ({ event_id: p.event_id, ends_at: p.ends_at })) }, { userType: 'admin' });
      if (!r?.success) throw r;
      setMsg({ text: `Sélection enregistrée (${r.count} événement${r.count > 1 ? 's' : ''}).` }); await load();
    } catch (e) { setMsg({ err: true, text: e?.error || e?.data?.error || 'Enregistrement impossible' }); }
    finally { setSaving(false); }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: 0 }}>⭐ À l'affiche de la Billetterie</h3>
          <p style={hint}>Jusqu'à {MAX} événements à venir par ville, mis en avant en tête de la Billetterie de l'app client. Si la sélection est incomplète, l'app la complète avec les prochains événements de la ville.</p>
        </div>
        <select className="filter-select" value={city} onChange={(e) => setCity(e.target.value)}>
          {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
      </div>

      {msg && <div style={{ ...banner, background: msg.err ? '#fef2f2' : '#ecfdf5', color: msg.err ? '#dc2626' : '#059669' }}>{msg.text}</div>}
      {loading ? <p style={hint}>Chargement…</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginTop: 12 }}>
          <div>
            <h4 style={sub}>Sélection ({picks.length}/{MAX}) — dans l'ordre d'affichage</h4>
            {!picks.length && <p style={hint}>Aucun événement choisi : l'app montre les prochains événements de la ville.</p>}
            {picks.map((p, i) => (
              <div key={p.event_id} style={{ ...row, opacity: p.live === false ? 0.55 : 1 }}>
                <span style={rank}>{i + 1}</span>
                {p.event?.poster ? <img src={p.event.poster} alt="" style={thumb} /> : <div style={{ ...thumb, display: 'grid', placeItems: 'center' }}>🎫</div>}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{p.event?.title || p.event_id}</div>
                  <div style={hint}>{p.event ? fmt(p.event.starts_at) : ''}{p.event?.organizer_name ? ` · ${p.event.organizer_name}` : ''}{p.live === false ? ' · ⚠️ plus affiché (passé, annulé, complet ou fin dépassée)' : ''}</div>
                  <label style={{ ...hint, display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    Fin de mise en avant (facultatif)
                    <input type="datetime-local" value={toLocalInput(p.ends_at)} onChange={(e) => setEnd(p.event_id, e.target.value)} style={{ padding: '3px 6px', border: '1px solid #DCE0E6', borderRadius: 6 }} />
                  </label>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <button style={iconBtn} disabled={i === 0} onClick={() => move(i, -1)} title="Monter">▲</button>
                  <button style={iconBtn} disabled={i === picks.length - 1} onClick={() => move(i, 1)} title="Descendre">▼</button>
                </div>
                <button style={{ ...iconBtn, color: '#dc2626' }} onClick={() => remove(p.event_id)} title="Retirer">✕</button>
              </div>
            ))}
            <button style={{ ...saveBtn, opacity: dirty && !saving ? 1 : 0.5 }} disabled={!dirty || saving} onClick={save}>{saving ? 'Enregistrement…' : 'Enregistrer la sélection'}</button>
          </div>
          <div>
            <h4 style={sub}>Événements à venir dans la ville ({candidates.length})</h4>
            {!candidates.length && <p style={hint}>Aucun événement publié à venir dans cette ville.</p>}
            <div style={{ maxHeight: 460, overflowY: 'auto' }}>
              {candidates.map((ev) => (
                <div key={ev.event_id} style={row}>
                  {ev.poster ? <img src={ev.poster} alt="" style={thumb} /> : <div style={{ ...thumb, display: 'grid', placeItems: 'center' }}>🎫</div>}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{ev.title}</div>
                    <div style={hint}>{fmt(ev.starts_at)}{ev.venue ? ` · ${ev.venue}` : ''}{ev.status === 'soldout' ? ' · complet' : ''}</div>
                  </div>
                  <button style={{ ...addBtn, opacity: pickedIds.has(ev.event_id) || picks.length >= MAX || ev.status !== 'published' ? 0.4 : 1 }}
                    disabled={pickedIds.has(ev.event_id) || picks.length >= MAX || ev.status !== 'published'} onClick={() => add(ev)}>
                    {pickedIds.has(ev.event_id) ? 'Choisi' : 'Ajouter'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const card = { background: '#fff', borderRadius: 14, padding: '1.25rem', marginBottom: '1.25rem', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' };
const hint = { fontSize: '0.8rem', color: '#5C636E', margin: '4px 0 0' };
const sub = { margin: '0 0 8px', fontSize: '0.92rem', color: '#1E2229' };
const banner = { padding: '10px 14px', borderRadius: 10, marginTop: 12, fontWeight: 600 };
const row = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', border: '1px solid #E9EBEF', borderRadius: 10, marginBottom: 8 };
const rank = { width: 22, height: 22, borderRadius: 11, background: '#1E2229', color: '#fff', fontSize: 12, fontWeight: 800, display: 'grid', placeItems: 'center', flex: '0 0 auto' };
const thumb = { width: 44, height: 58, objectFit: 'cover', borderRadius: 6, background: '#F4F5F7', flex: '0 0 auto' };
const iconBtn = { background: '#F4F5F7', border: '1px solid #E9EBEF', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 12 };
const addBtn = { background: '#F54119', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' };
const saveBtn = { background: '#F54119', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', marginTop: 8 };
